import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type NavigateOptions, useNavigate } from "react-router-dom";
import type { CashierTradingAccountMargin } from "@/api/cashier";
import {
  internalTransferConfirmMutation,
  internalTransferCreateMutation,
} from "@/api/cashier/@tanstack/react-query.gen";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { ConfirmTransferModal } from "@/components/modals/confirm-transfer-modal/ConfirmTransferModal";
import { Section } from "@/components/section/Section";
import { useAccount } from "@/hooks/transfer/useAccount";
import { useAmountForm } from "@/hooks/transfer/useAmountForm";
import {
  FunnelFlow,
  FunnelStep,
  trackFunnelStep,
} from "@/observability/funnel";
import style from "@/pages/transfer/internal-transfer/style.module.css";
import { GLOBAL_NAVIGATION_URLS, globalNavigation } from "@/utils/constants";
import { ECashierFlows, ERoutes } from "@/utils/enums";
import { AccountSelectionWithAmountSection } from "../accoount-selection-with-amount-section/AccountSelectionWithAmountSection";
import { TransferIsNotAvailable } from "./TransferIsNotAvailable";
import { useTradingAccounts } from "@/hooks/transfer/useTradingAccounts";
import {
  majorToAmountCurrencyRequest,
  moneyToMajorUnits,
} from "@/utils/helpers";

const noAvailableBalance = (account: CashierTradingAccountMargin) =>
  (moneyToMajorUnits(account.margin) ?? 0) <= 0;

export const InternalTransfer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { amountFormData } = useAmountForm();

  const { selectedAccount, selectedToAccount } = useAccount();

  const selectedAccountCurrency = selectedAccount?.currency;
  const selectedAccountToCurrency = selectedToAccount?.currency;

  const { tradingAccounts, error, isLoading } = useTradingAccounts();

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once on mount
  useEffect(() => {
    trackFunnelStep(FunnelFlow.Transfer, FunnelStep.Intent);
  }, []);

  const [transferReferenceId, setTransferReferenceId] = useState<null | string>(
    null
  );

  const [accountSelectionError, setAccountSelectionError] =
    useState<string>("");

  const tradingAccountFromList = tradingAccounts.filter(
    (account) => account.id !== selectedToAccount?.id
  );

  const tradingAccountToList = tradingAccounts.filter(
    (account) => account.id !== selectedAccount?.id && account.isFundable
  );

  const completionPageState: NavigateOptions = {
    state: {
      accountId: selectedAccount?.id,
      toAccountId: selectedToAccount?.id,
      processingValue: amountFormData.processingValue,
      tradingValue: amountFormData.tradingValue,
    },
  };

  const {
    mutate: createInternalTransfer,
    isPending: internalTransferCreationLoading,
  } = useMutation({
    ...internalTransferCreateMutation(),
    onError: () => {
      navigate(
        `/${ECashierFlows.Transfer}/${ERoutes.CompletionFailed}/${transferReferenceId}`,
        completionPageState
      );
    },
    onSuccess: (response) => {
      if (response?.reference_id) {
        setTransferReferenceId(response.reference_id as string);
      }
    },
  });

  const confirmInFlightRef = useRef(false);

  const {
    mutate: confirmInternalTransfer,
    isPending: internalTransferConfirmationLoading,
  } = useMutation({
    ...internalTransferConfirmMutation(),
    // Use the reference_id from the mutation variables rather than the
    // transferReferenceId state: the modal can close (state reset to null via
    // onClose, e.g. the 60s timeout) or a new transfer can start while the
    // confirm request is in flight, which would otherwise navigate to .../null.
    onError: (_error, variables) => {
      confirmInFlightRef.current = false;
      navigate(
        `/${ECashierFlows.Transfer}/${ERoutes.CompletionFailed}/${variables.body?.reference_id}`,
        completionPageState
      );
    },
    onSuccess: (data, variables) => {
      confirmInFlightRef.current = false;
      navigate(
        `/${ECashierFlows.Transfer}/${ERoutes.CompletionSuccess}/${variables.body?.reference_id}`,
        {
          state: {
            ...completionPageState.state,
            sourceAccountBalance: data.source_balance,
            targetAccountBalance: data.target_balance,
          },
        }
      );
    },
  });

  const handleCreateTransferRequest = () => {
    if (!selectedAccount || !selectedToAccount) return;
    trackFunnelStep(FunnelFlow.Transfer, FunnelStep.ExecuteClicked);
    createInternalTransfer({
      body: {
        amount_with_currency: majorToAmountCurrencyRequest(
          amountFormData.processingValue,
          selectedAccount.currency
        ),
        source_id: selectedAccount.id,
        target_id: selectedToAccount.id,
      },
    });
  };

  const handleConfirmTransfer = () => {
    // Ref guard is synchronous — isPending only updates on the next render, so
    // it alone cannot stop a rapid second click from firing the mutation again.
    if (!transferReferenceId || confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    confirmInternalTransfer({
      body: {
        reference_id: transferReferenceId,
      },
    });
  };

  if (error) return <TransferIsNotAvailable reason="request_error" />;
  if (!isLoading && (!tradingAccounts.length || tradingAccounts.length < 2)) {
    return <TransferIsNotAvailable reason="no_accounts" />;
  }
  if (
    !isLoading &&
    tradingAccounts &&
    tradingAccounts.every(noAvailableBalance)
  ) {
    return <TransferIsNotAvailable reason="no_funds" />;
  }

  return (
    <Section className={style["transfer-content"]}>
      <AccountSelectionWithAmountSection
        tradingAccountList={tradingAccountFromList || []}
        tradingAccountToList={tradingAccountToList || []}
        onError={setAccountSelectionError}
      />

      <NavButtons
        backAction={() =>
          globalNavigation(GLOBAL_NAVIGATION_URLS.TRADING_ACCOUNTS, true)
        }
        nextAction={handleCreateTransferRequest}
        nextDisabled={
          !selectedAccount ||
          !selectedToAccount ||
          accountSelectionError !== "" ||
          !amountFormData.processingValue ||
          internalTransferCreationLoading
        }
      />

      {selectedAccountCurrency && selectedAccountToCurrency && (
        <ConfirmTransferModal
          isOpen={!!transferReferenceId}
          onClose={() => setTransferReferenceId(null)}
          data-testid="confirm-transfer-modal"
          onConfirm={handleConfirmTransfer}
          isConfirmLoading={internalTransferConfirmationLoading}
          fromTransferData={{
            account: selectedAccount,
            currency: selectedAccountCurrency,
            amount: amountFormData.processingValue,
          }}
          toTransferData={{
            account: selectedToAccount,
            currency: selectedAccountToCurrency,
            amount: amountFormData.tradingValue,
          }}
          arrivalTime={t("transfer.arrival_time_instant")}
          exchangeRate={{
            from_currency_code: selectedAccountCurrency.code,
            to_currency_code: selectedAccountToCurrency.code,
            rate: amountFormData.rate,
          }}
        />
      )}
    </Section>
  );
};
